
import React, { useEffect, useState } from "react";

const blank = {
  tenantName: "",
  phone: "",
  roomId: "",
  checkIn: "",
  checkOut: "",
  status: "CONFIRMED",
  guests: 1,
  notes: "",
  paymentStatus: "UNPAID",
  amountPaid: 0,
  totalAmount: 0,
  loyaltyTier: "REGULAR",
};

export default function StayDetailsDrawer({
  open,
  stay,
  rooms = [],
  mode = "view",
  onClose,
  onSave,
  onCheckIn,
  onCheckOut,
}) {
  const [form, setForm] = useState(blank);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(
      stay
        ? { ...blank, ...stay }
        : {
            ...blank,
            checkIn: new Date().toISOString().slice(0, 10),
          }
    );
    setError("");
  }, [stay, open]);

  if (!open) return null;

  const editing = mode === "edit" || mode === "create";

  const set = (key, value) => {
    setForm((old) => ({ ...old, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (
      !form.tenantName.trim() ||
      !form.roomId ||
      !form.checkIn ||
      !form.checkOut
    ) {
      setError("Tenant, room and dates are required.");
      return;
    }

    if (form.checkOut <= form.checkIn) {
      setError("Check-out must be after check-in.");
      return;
    }

    const totalAmount = Number(form.totalAmount || 0);
    const amountPaid = Number(form.amountPaid || 0);

    if (totalAmount < 0 || amountPaid < 0) {
      setError("Payment amounts cannot be negative.");
      return;
    }

    if (amountPaid > totalAmount) {
      setError("Amount paid cannot exceed the total amount.");
      return;
    }

    try {
      await onSave({
        ...form,
        guests: Number(form.guests || 1),
        totalAmount,
        amountPaid,
      });
    } catch (err) {
      setError(err.message || "Unable to save stay.");
    }
  };

  const paymentStatus = String(
    form.paymentStatus || "UNPAID"
  ).toUpperCase();

  const loyaltyTier = String(
    form.loyaltyTier || "REGULAR"
  ).toUpperCase();

  const paymentLabel = {
    PAID: "Paid",
    PARTIAL: "Partially paid",
    UNPAID: "Unpaid",
  }[paymentStatus] || paymentStatus;

  const loyaltyLabel = {
    VIP: "♛ VIP guest",
    LOYAL: "★ Loyal guest",
    REGULAR: "Regular guest",
  }[loyaltyTier] || "Regular guest";

  return (
    <div
      className="occ-drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <aside
        className="occ-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Stay information"
      >
        <div className="occ-drawer-header">
          <div>
            <div className="occ-eyebrow">STAY INFORMATION</div>
            <h2>
              {editing
                ? mode === "create"
                  ? "Create stay"
                  : "Edit stay"
                : stay?.tenantName || "Stay details"}
            </h2>
          </div>

          <button
            type="button"
            className="occ-icon-btn"
            onClick={onClose}
            aria-label="Close drawer"
          >
            ×
          </button>
        </div>

        <div className="occ-guest-profile">
          <div className="occ-guest-avatar">
            {(form.tenantName || "G")
              .trim()
              .charAt(0)
              .toUpperCase()}
          </div>

          <div className="occ-guest-profile-info">
            <strong>{form.tenantName || "Guest name"}</strong>

            <div className="occ-guest-badges">
              <span
                className={`occ-payment-badge is-${paymentStatus.toLowerCase()}`}
              >
                {paymentLabel}
              </span>

              <span
                className={`occ-loyalty-badge is-${loyaltyTier.toLowerCase()}`}
              >
                {loyaltyLabel}
              </span>
            </div>

            <small>
              {Number(form.guests || 1)} guest
              {Number(form.guests || 1) !== 1 ? "s" : ""}
            </small>
          </div>
        </div>

        <form
          className="occ-drawer-form"
          onSubmit={submit}
        >
          <label>
            Tenant name
            <input
              disabled={!editing}
              value={form.tenantName || ""}
              onChange={(event) =>
                set("tenantName", event.target.value)
              }
            />
          </label>

          <label>
            Phone number
            <input
              disabled={!editing}
              value={form.phone || ""}
              onChange={(event) =>
                set("phone", event.target.value)
              }
            />
          </label>

          <label>
            Room
            <select
              disabled={!editing}
              value={form.roomId || ""}
              onChange={(event) =>
                set("roomId", event.target.value)
              }
            >
              <option value="">Select room</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.number} · {room.type} · capacity{" "}
                  {room.capacity}
                </option>
              ))}
            </select>
          </label>

          <div className="occ-form-two">
            <label>
              Check-in
              <input
                type="date"
                disabled={!editing}
                value={form.checkIn || ""}
                onChange={(event) =>
                  set("checkIn", event.target.value)
                }
              />
            </label>

            <label>
              Check-out
              <input
                type="date"
                disabled={!editing}
                value={form.checkOut || ""}
                onChange={(event) =>
                  set("checkOut", event.target.value)
                }
              />
            </label>
          </div>

          <div className="occ-form-two">
            <label>
              Guests
              <input
                type="number"
                min="1"
                disabled={!editing}
                value={form.guests ?? 1}
                onChange={(event) =>
                  set("guests", event.target.value)
                }
              />
            </label>

            <label>
              Booking status
              <select
                disabled={!editing}
                value={form.status || "CONFIRMED"}
                onChange={(event) =>
                  set("status", event.target.value)
                }
              >
                {[
                  "PENDING",
                  "CONFIRMED",
                  "CHECKED_IN",
                  "CHECKED_OUT",
                  "CANCELLED",
                ].map((status) => (
                  <option key={status} value={status}>
                    {status.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="occ-form-two">
            <label>
              Payment status
              <select
                disabled={!editing}
                value={form.paymentStatus || "UNPAID"}
                onChange={(event) =>
                  set("paymentStatus", event.target.value)
                }
              >
                <option value="UNPAID">Unpaid</option>
                <option value="PARTIAL">Partially paid</option>
                <option value="PAID">Paid</option>
              </select>
            </label>

            <label>
              Loyalty level
              <select
                disabled={!editing}
                value={form.loyaltyTier || "REGULAR"}
                onChange={(event) =>
                  set("loyaltyTier", event.target.value)
                }
              >
                <option value="REGULAR">Regular</option>
                <option value="LOYAL">Loyal</option>
                <option value="VIP">VIP</option>
              </select>
            </label>
          </div>

          <div className="occ-form-two">
            <label>
              Total amount
              <input
                type="number"
                min="0"
                disabled={!editing}
                value={form.totalAmount ?? 0}
                onChange={(event) =>
                  set("totalAmount", event.target.value)
                }
              />
            </label>

            <label>
              Amount paid
              <input
                type="number"
                min="0"
                disabled={!editing}
                value={form.amountPaid ?? 0}
                onChange={(event) =>
                  set("amountPaid", event.target.value)
                }
              />
            </label>
          </div>

          <label>
            Notes
            <textarea
              rows="3"
              disabled={!editing}
              value={form.notes || ""}
              onChange={(event) =>
                set("notes", event.target.value)
              }
            />
          </label>

          {error && (
            <div className="occ-form-error" role="alert">
              {error}
            </div>
          )}

          <div className="occ-drawer-actions">
            {editing ? (
              <>
                <button
                  type="button"
                  className="occ-secondary-btn"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="occ-primary-btn"
                >
                  Save stay
                </button>
              </>
            ) : (
              <>
                {stay?.status === "CONFIRMED" && (
                  <button
                    type="button"
                    className="occ-primary-btn"
                    onClick={() => onCheckIn?.(stay)}
                  >
                    Check in
                  </button>
                )}

                {stay?.status === "CHECKED_IN" && (
                  <button
                    type="button"
                    className="occ-primary-btn"
                    onClick={() => onCheckOut?.(stay)}
                  >
                    Check out
                  </button>
                )}

                <button
                  type="button"
                  className="occ-secondary-btn"
                  onClick={() => onSave?.(form, "edit")}
                >
                  Edit stay
                </button>
              </>
            )}
          </div>
        </form>
      </aside>
    </div>
  );
}
